import { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';

interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isIPad: boolean;
  isIOS: boolean;
  isDesktop: boolean;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  userAgent: string;
  screenSize: {
    width: number;
    height: number;
  };
  orientation: 'portrait' | 'landscape';
}

export default function useDeviceDetection(): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    const userAgent = navigator.userAgent;
    const { innerWidth: width, innerHeight: height } = window;

    return {
      isMobile: false,
      isTablet: false,
      isIPad: false,
      isIOS: false,
      isDesktop: false,
      deviceType: 'desktop',
      userAgent,
      screenSize: { width, height },
      orientation: width > height ? 'landscape' : 'portrait'
    };
  });

  // Detección usando react-responsive
  const isMobileScreen = useMediaQuery({ maxWidth: 768 });
  const isTabletScreen = useMediaQuery({ minWidth: 769, maxWidth: 1024 });
  const isDesktopScreen = useMediaQuery({ minWidth: 1025 });

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const { innerWidth: width, innerHeight: height } = window;

    // Detección de iPad específica
    const isIPad = /ipad/.test(userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    const isIOS = /iphone|ipad|ipod/i.test(userAgent);

    // Detección de dispositivos móviles
    const isMobile = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent) ||
      (width <= 768 && 'ontouchstart' in window);

    // Detección de tablets (excluyendo iPads)
    const isTablet = (width > 768 && width <= 1024 && 'ontouchstart' in window) ||
      /tablet|ipad|playbook|silk/i.test(userAgent);

    // Detección de escritorio
    const isDesktop = !isMobile && !isTablet && !isIPad;

    // Determinar tipo de dispositivo
    let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
    if (isMobile) deviceType = 'mobile';
    else if (isTablet || isIPad) deviceType = 'tablet';

    setDeviceInfo({
      isMobile,
      isTablet: isTablet || isIPad,
      isIPad,
      isIOS,
      isDesktop,
      deviceType,
      userAgent: navigator.userAgent,
      screenSize: { width, height },
      orientation: width > height ? 'landscape' : 'portrait'
    });
  }, [isMobileScreen, isTabletScreen, isDesktopScreen]);

  // Escuchar cambios de orientación y redimensionamiento
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const { innerWidth: width, innerHeight: height } = window;
        setDeviceInfo(prev => ({
          ...prev,
          screenSize: { width, height },
          orientation: width > height ? 'landscape' : 'portrait'
        }));
      }, 200);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return deviceInfo;
}

// Hook simplificado solo para móvil
export function useIsMobile(): boolean {
  const deviceInfo = useDeviceDetection();
  return deviceInfo.isMobile;
}

// Hook simplificado solo para iPad
export function useIsIPad(): boolean {
  const deviceInfo = useDeviceDetection();
  return deviceInfo.isIPad;
}

// Hook simplificado para tablet (incluye iPad)
export function useIsTablet(): boolean {
  const deviceInfo = useDeviceDetection();
  return deviceInfo.isTablet;
}
